import React, { useState, useMemo } from 'react';
import CalcShell, { StepsPanel } from '../CalcShell';
import { fmt } from './_finHelpers';

// Salary Calculator — convert between hourly, daily, weekly, biweekly, semi-monthly, monthly, quarterly, annual
export default function SalaryCalc() {
  const [amount, setAmount] = useState(30);
  const [freq, setFreq] = useState('hourly');
  const [hoursPerWeek, setHoursPerWeek] = useState(40);
  const [daysPerWeek, setDaysPerWeek] = useState(5);
  const [holidays, setHolidays] = useState(10);
  const [vacation, setVacation] = useState(15);

  const c = useMemo(() => {
    const workDaysPerYear = 52 * daysPerWeek - holidays - vacation;
    const workHoursPerYear = workDaysPerYear * (hoursPerWeek / daysPerWeek);
    // Convert input to annual first
    let annual = 0;
    switch (freq) {
      case 'hourly': annual = amount * workHoursPerYear; break;
      case 'daily': annual = amount * workDaysPerYear; break;
      case 'weekly': annual = amount * 52; break;
      case 'biweekly': annual = amount * 26; break;
      case 'semimonthly': annual = amount * 24; break;
      case 'monthly': annual = amount * 12; break;
      case 'quarterly': annual = amount * 4; break;
      case 'annual': annual = amount; break;
      default: annual = 0;
    }
    const unadjusted = amount * (freq === 'hourly' ? 40 * 52 : (freq === 'daily' ? 5*52 : (freq === 'weekly' ? 52 : (freq === 'biweekly' ? 26 : (freq === 'semimonthly' ? 24 : (freq === 'monthly' ? 12 : (freq === 'quarterly' ? 4 : 1)))))));
    return {
      hourly: annual / workHoursPerYear,
      daily: annual / workDaysPerYear,
      weekly: annual / 52,
      biweekly: annual / 26,
      semimonthly: annual / 24,
      monthly: annual / 12,
      quarterly: annual / 4,
      annual,
      unadjustedAnnual: unadjusted,
      workDaysPerYear,
      workHoursPerYear,
    };
  }, [amount, freq, hoursPerWeek, daysPerWeek, holidays, vacation]);

  return (
    <CalcShell look="cnet" title="Salary Calculator" subtitle="Convert salary between hourly, daily, weekly, bi-weekly, monthly, and annual — adjusted for holidays and vacation."
      about={<><p>The <b>Salary Calculator</b> converts wages between different pay frequencies. Adjusted values account for unpaid holidays and vacation days; unadjusted values assume every week is a working week.</p></>}
    >
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div>
          <div className="field"><label>Salary amount</label><div className="flex gap-2"><span className="self-center text-slate-500">$</span><input type="number" value={amount} onChange={e=>setAmount(+e.target.value||0)} className="flex-1"/></div></div>
          <div className="field"><label>Pay frequency</label>
            <select value={freq} onChange={e=>setFreq(e.target.value)}>
              <option value="hourly">Hourly</option>
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
              <option value="biweekly">Bi-weekly (every 2 weeks)</option>
              <option value="semimonthly">Semi-monthly (twice a month)</option>
              <option value="monthly">Monthly</option>
              <option value="quarterly">Quarterly</option>
              <option value="annual">Annual</option>
            </select>
          </div>
          <div className="field"><label>Hours per week</label><input type="number" value={hoursPerWeek} onChange={e=>setHoursPerWeek(+e.target.value||0)}/></div>
          <div className="field"><label>Days per week</label><input type="number" value={daysPerWeek} onChange={e=>setDaysPerWeek(+e.target.value||0)}/></div>
          <div className="field"><label>Holidays per year</label><input type="number" value={holidays} onChange={e=>setHolidays(+e.target.value||0)}/></div>
          <div className="field"><label>Vacation days per year</label><input type="number" value={vacation} onChange={e=>setVacation(+e.target.value||0)}/></div>
        </div>
        <div>
          <table className="data-table">
            <thead><tr><th></th><th className="text-right">Unadjusted</th><th className="text-right">Adjusted (holidays + vacation)</th></tr></thead>
            <tbody>
              <tr><td>Hourly</td><td className="text-right">{fmt(c.unadjustedAnnual / (40*52))}</td><td className="text-right font-semibold" data-testid="salary-hourly">{fmt(c.hourly)}</td></tr>
              <tr><td>Daily</td><td className="text-right">{fmt(c.unadjustedAnnual / (5*52))}</td><td className="text-right">{fmt(c.daily)}</td></tr>
              <tr><td>Weekly</td><td className="text-right">{fmt(c.unadjustedAnnual / 52)}</td><td className="text-right">{fmt(c.weekly)}</td></tr>
              <tr><td>Bi-weekly</td><td className="text-right">{fmt(c.unadjustedAnnual / 26)}</td><td className="text-right">{fmt(c.biweekly)}</td></tr>
              <tr><td>Semi-monthly</td><td className="text-right">{fmt(c.unadjustedAnnual / 24)}</td><td className="text-right">{fmt(c.semimonthly)}</td></tr>
              <tr><td>Monthly</td><td className="text-right">{fmt(c.unadjustedAnnual / 12)}</td><td className="text-right">{fmt(c.monthly)}</td></tr>
              <tr><td>Quarterly</td><td className="text-right">{fmt(c.unadjustedAnnual / 4)}</td><td className="text-right">{fmt(c.quarterly)}</td></tr>
              <tr className="border-t-2 font-semibold"><td>Annual</td><td className="text-right">{fmt(c.unadjustedAnnual)}</td><td className="text-right text-blue-700" data-testid="salary-annual">{fmt(c.annual)}</td></tr>
            </tbody>
          </table>
          <p className="text-xs text-slate-500 mt-3">Adjusted values based on <b>{c.workDaysPerYear}</b> working days and <b>{c.workHoursPerYear.toFixed(0)}</b> working hours per year.</p>
        </div>
      </div>
    </CalcShell>
  );
}
